import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AssignmentIcon from "@mui/icons-material/Assignment";

const ManageFillsDialog = ({ open, onClose, tradeData, onSubmit }) => {
  const theme = useTheme();
  const [filledShares, setFilledShares] = useState("");
  const [pricePerShare, setPricePerShare] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    // Validate inputs
    if (!filledShares || !pricePerShare) {
      setError("Both fields are required");
      return;
    }

    const shares = parseFloat(filledShares);
    const price = parseFloat(pricePerShare);

    if (isNaN(shares) || shares <= 0) {
      setError("Filled Shares must be a positive number");
      return;
    }

    if (isNaN(price) || price <= 0) {
      setError("Price per Share must be a positive number");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      await onSubmit({
        sharecount: Math.round(shares), // Ensure it's an integer
        shareprice: price,
        trade: tradeData.tradeId,
        user: tradeData.user || "asfand@plutus21.com" // Default user if not provided
      });
      
      // Reset form on success
      setFilledShares("");
      setPricePerShare("");
      onClose();
    } catch (err) {
      setError("Failed to submit fills. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFilledShares("");
      setPricePerShare("");
      setError("");
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    // Allow only numbers, decimal point, and backspace
    if (!/[0-9.]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete") {
      e.preventDefault();
    }
    
    // Prevent multiple decimal points
    if (e.key === "." && e.target.value.includes(".")) {
      e.preventDefault();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 2,
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: "16px"
      }}>
        <AssignmentIcon sx={{ color: "secondary.main" }} />
        Manage Fills
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ mb: 1, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add fill information for trade: <strong>{tradeData?.ticker}</strong>
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <TextField
            label="Filled Shares"
            type="text"
            value={filledShares}
            onChange={(e) => setFilledShares(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter number of shares"
            fullWidth
            disabled={isSubmitting}
            InputProps={{
              endAdornment: (
                <Typography variant="body2" color="text.secondary">
                  shares
                </Typography>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />

          <TextField
            label="Price per Share"
            type="text"
            value={pricePerShare}
            onChange={(e) => setPricePerShare(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter price per share"
            fullWidth
            disabled={isSubmitting}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        pb: 3, 
        borderTop: `1px solid ${theme.palette.divider}`,
        pt: 2
      }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !filledShares || !pricePerShare}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            backgroundColor: "secondary.main",
            "&:hover": {
              backgroundColor: "secondary.dark",
            },
            "&:disabled": {
              backgroundColor: "action.disabledBackground",
            }
          }}
        >
          {isSubmitting ? "Adding..." : "Add Fills"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageFillsDialog;
